import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingUp, Users, ShoppingCart, Globe, ArrowRight, Store, Utensils } from "lucide-react";

const caseStudies = [
  {
    id: 1,
    client: "Restaurant Khay Ahmed",
    industry: "Food & Beverage",
    icon: Utensils,
    website: "restaurantkhayahmed.com",
    description: "Family restaurant in Martil with 20+ years of culinary excellence",
    challenge: "Traditional restaurant struggling with digital ordering and online presence",
    solution: "Custom website with integrated online ordering system (Glovo-like platform) + comprehensive social media management",
    results: [
      { metric: "300%", label: "Increase in orders" },
      { metric: "5x", label: "Online presence growth" },
      { metric: "250%", label: "Revenue increase" },
    ],
    services: ["Web Development", "Online Ordering Platform", "Social Media Management", "Digital Marketing"],
    testimonial: "Going digital transformed our business. Orders tripled and we reached customers we never could before.",
    image: "https://images.unsplash.com/photo-1514326640560-7d063ef2aed5?q=80&w=2070&auto=format&fit=crop",
    color: "from-orange-500/20 to-red-500/20"
  },
  {
    id: 2,
    client: "Aboujoud",
    industry: "Fashion & Retail",
    icon: ShoppingCart,
    website: "aboujoud.ma",
    description: "Premium women's fashion boutique specializing in elegant wear",
    challenge: "Small shop with limited reach and no online presence",
    solution: "Full social media management + e-commerce website development + digital marketing campaigns",
    results: [
      { metric: "2nd Shop", label: "Business expansion" },
      { metric: "400%", label: "Customer base growth" },
      { metric: "Fully Digital", label: "Business transformation" },
    ],
    services: ["Social Media Management", "E-commerce Development", "Digital Marketing", "Brand Strategy"],
    testimonial: "We went from one small shop to opening our second location. The digital transformation was life-changing.",
    image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=2070&auto=format&fit=crop",
    color: "from-purple-500/20 to-pink-500/20"
  },
  {
    id: 3,
    client: "Le Monde Du Caftan",
    industry: "Fashion & Traditional Wear",
    icon: Store,
    website: "lemondeducaftan.ma",
    description: "Moroccan traditional clothing designer and manufacturer",
    challenge: "Traditional business model with no digital presence or online sales",
    solution: "Complete e-commerce platform with custom design options + social media strategy",
    results: [
      { metric: "500+", label: "Monthly orders" },
      { metric: "International", label: "Market reach" },
      { metric: "350%", label: "Sales increase" },
    ],
    services: ["E-commerce Development", "Social Media Management", "SEO", "Content Creation"],
    image: "https://images.unsplash.com/photo-1509319117443-c5a1069d7ecb?q=80&w=2070&auto=format&fit=crop",
    color: "from-blue-500/20 to-cyan-500/20"
  },
  {
    id: 4,
    client: "Diva Driver",
    industry: "Transportation & Services",
    icon: Globe,
    website: "divadriver.ma",
    description: "Luxury chauffeur service driven by women, exclusively for elite clientele",
    challenge: "New business needing complete digital presence and booking system",
    solution: "Professional website with WhatsApp integration + complete digital branding + social media launch",
    results: [
      { metric: "200+", label: "Monthly bookings" },
      { metric: "Premium", label: "Brand positioning" },
      { metric: "5★", label: "Client satisfaction" },
    ],
    services: ["Web Development", "Brand Strategy", "Social Media Launch", "Digital Marketing"],
    image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?q=80&w=2070&auto=format&fit=crop",
    color: "from-emerald-500/20 to-teal-500/20"
  },
  {
    id: 5,
    client: "Asmaa Naciri",
    industry: "Coaching & Personal Development",
    icon: Users,
    website: "asmaanaciri.ma",
    description: "Personal development coach and pedagogical director with 20+ years experience",
    challenge: "Established coach needing professional digital presence for workshop bookings",
    solution: "Professional website with booking system + social media strategy + content marketing",
    results: [
      { metric: "300+", label: "Workshop participants" },
      { metric: "10x", label: "Online visibility" },
      { metric: "Regional", label: "Recognition achieved" },
    ],
    services: ["Web Development", "Social Media Management", "Content Marketing", "SEO"],
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=2069&auto=format&fit=crop",
    color: "from-rose-500/20 to-pink-500/20"
  },
  {
    id: 6,
    client: "Queenury",
    industry: "Jewelry & Accessories",
    icon: ShoppingCart,
    website: "queenury.shop",
    description: "Premium jewelry e-commerce specializing in elegant earrings and accessories",
    challenge: "New brand launching online with no digital presence or sales channel",
    solution: "Complete e-commerce setup + brand identity + social media launch + digital advertising campaigns",
    results: [
      { metric: "1000+", label: "Monthly customers" },
      { metric: "International", label: "Shipping enabled" },
      { metric: "4.9★", label: "Customer rating" },
    ],
    services: ["E-commerce Development", "Brand Strategy", "Social Media Management", "Paid Advertising"],
    testimonial: "Spotlight Adz built our entire online presence from scratch. The results exceeded all expectations.",
    image: "https://images.unsplash.com/photo-1535632787350-4e68ef0ac584?q=80&w=2070&auto=format&fit=crop",
    color: "from-amber-500/20 to-yellow-500/20"
  },
  {
    id: 7,
    client: "Dolcemisu",
    industry: "Artisan Desserts & Tiramisu",
    icon: Utensils,
    website: "dolcemisu.ma",
    description: "Artisan tiramisu boutique crafting authentic Italian desserts with a Moroccan touch",
    challenge: "Small tiramisu business with exceptional products but zero online presence and limited local reach",
    solution: "Custom-built website showcasing their artisan process + comprehensive digital marketing strategy + ongoing social media management + SEO optimization for local discovery",
    results: [
      { metric: "Best Website", label: "In dessert category" },
      { metric: "Ongoing Growth", label: "Marketing strategy" },
      { metric: "Expanding", label: "Customer base" },
    ],
    services: ["Web Development", "Ongoing Marketing", "Social Media Management", "SEO", "Content Creation", "Growth Strategy"],
    testimonial: "They created the perfect website for our tiramisu business and continue to help us grow every month.",
    image: "https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?q=80&w=2070&auto=format&fit=crop",
    color: "from-pink-500/20 to-orange-500/20"
  }
];

export const CaseStudies = () => {
  return (
    <section id="case-studies" className="relative z-10 responsive-container py-20">
      <div className="text-center mb-16 space-y-4">
        <Badge variant="outline" className="mb-4">
          <TrendingUp className="h-3 w-3 mr-2" />
          Success Stories
        </Badge>
        <h2 className="text-4xl md:text-5xl font-bold text-foreground">
          Real Results from Real Businesses
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          See how we've helped Moroccan businesses transform digitally and achieve remarkable growth
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mb-12">
        {caseStudies.map((study, index) => {
          const Icon = study.icon;
          return (
            <Card 
              key={study.id} 
              className="p-8 hover:shadow-2xl transition-all duration-300 border-border/50 glass-card group overflow-hidden relative"
            >
              {/* Background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${study.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              
              <div className="relative z-10 space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full w-14 h-14 bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors flex-shrink-0">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-1">{study.client}</h3>
                      <p className="text-sm text-muted-foreground mb-2">{study.industry}</p>
                      <a 
                        href={`https://${study.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <Globe className="h-3 w-3" />
                        {study.website}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-muted-foreground">{study.description}</p>

                {/* Challenge & Solution */}
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">Challenge</h4>
                    <p className="text-sm text-muted-foreground">{study.challenge}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">Our Solution</h4>
                    <p className="text-sm text-muted-foreground">{study.solution}</p>
                  </div>
                </div>

                {/* Results */}
                <div className="grid grid-cols-3 gap-4 py-4 border-y border-border/50">
                  {study.results.map((result, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                        {result.metric}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">{result.label}</div>
                    </div>
                  ))}
                </div>

                {/* Services */}
                <div className="flex flex-wrap gap-2">
                  {study.services.map((service, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {service}
                    </Badge>
                  ))}
                </div>

                {/* Testimonial */}
                <blockquote className="border-l-4 border-primary pl-4 italic text-sm text-muted-foreground">
                  "{study.testimonial}"
                </blockquote>

                {/* CTA */}
                <Button 
                  variant="outline" 
                  className="w-full group/btn"
                  onClick={() => window.open(`https://${study.website}`, '_blank')}
                >
                  View Live Website
                  <ArrowRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <div className="text-center glass-card p-8 rounded-xl">
        <p className="text-lg text-muted-foreground mb-4">
          These are just a few examples. We've helped many more businesses across Morocco achieve digital success.
        </p>
        <Button 
          size="lg" 
          className="btn-primary gap-2"
          onClick={() => {
            const form = document.getElementById('service-request-form');
            form?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Start Your Success Story
          <ArrowRight className="h-5 w-5" />
        </Button>
      </div>
    </section>
  );
};